import { useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit, Eye, Package, Plus, Search, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Dropdown } from '@/components/ui/select'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Breadcrumbs, DetailList, Panel, StatTile } from '@/components/ui/detail-list'
import { DeviceForm } from '@/components/forms/DeviceForm'
import { DeviceModelForm } from '@/components/forms/DeviceModelForm'
import { AssignmentModal } from '@/components/modals/AssignmentModal'
import { apiClient } from '@/lib/api-client'
import { compactBarcode } from '@/lib/barcode'
import {
  canAssign,
  canReturn,
  compareAssets,
  returnAsset,
  ownershipLabel,
  powerDetail,
  powerFieldLabel,
  statusLabel,
  typeLabel,
} from '@/lib/device-format'
import { useAuth } from '@/hooks/useAuth'
import { formatDateShort, getStatusColor } from '@shared/utils'
import { toast } from 'react-hot-toast'

export function DeviceModelDetailPage() {
  const { modelId } = useParams()
  const { user } = useAuth()
  const isAdmin = user?.role === 'Admin'
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editingModel, setEditingModel] = useState(false)
  const [editAsset, setEditAsset] = useState<any>(null)
  const [assignTarget, setAssignTarget] = useState<{ id: number; label: string; assignment?: any } | null>(null)
  const [returningId, setReturningId] = useState<number | null>(null)

  const { data: model, isLoading } = useQuery({
    queryKey: ['device-model', modelId],
    queryFn: async () => (await apiClient.get(`/device-models/${modelId}`)).data,
    enabled: !!modelId,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['device-model', modelId] })
    queryClient.invalidateQueries({ queryKey: ['devices'] })
    queryClient.invalidateQueries({ queryKey: ['device-models'] })
  }

  const deleteAsset = useMutation({
    mutationFn: async (assetId: number) => apiClient.delete(`/devices/${assetId}`),
    onSuccess: () => {
      refresh()
      toast.success('Asset deleted')
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Delete failed'),
  })

  const assets: any[] = model?.assets || []

  const counts = useMemo(() => {
    return assets.reduce(
      (acc, asset) => {
        acc.total += 1
        if (asset.status === 'available') acc.available += 1
        else if (asset.status === 'assigned') acc.assigned += 1
        else if (asset.status === 'assigned_pending') acc.pending += 1
        else acc.out += 1
        return acc
      },
      { total: 0, available: 0, assigned: 0, pending: 0, out: 0 },
    )
  }, [assets])

  const visibleAssets = useMemo(() => {
    const term = compactBarcode(search)
    return assets.filter((asset) => {
      if (statusFilter !== 'all' && asset.status !== statusFilter) return false
      if (!term) return true
      const holder = asset.assignments?.[0]?.employee?.name || ''
      return (
        compactBarcode(asset.serialNumber || '').includes(term)
        || holder.toLowerCase().includes(search.trim().toLowerCase())
      )
    }).sort(compareAssets)
  }, [assets, search, statusFilter])

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!model) {
    return (
      <div className="py-16 text-center">
        <Package className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium">Device model not found</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/devices">Back to devices</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Breadcrumbs items={[{ label: 'Devices', to: '/devices' }, { label: model.name }]} />

      <div className="flex flex-col gap-3 border-b border-border/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight">{model.name}</h1>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditingModel(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit model
            </Button>
            <Button onClick={() => setIsAddOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add assets
            </Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatTile label="Total" value={counts.total} />
        <StatTile label="Available" value={counts.available} tone="text-emerald-600 dark:text-emerald-400" />
        <StatTile label="Assigned" value={counts.assigned} tone="text-sky-600 dark:text-sky-400" />
        <StatTile label="Pending approval" value={counts.pending} tone="text-amber-600 dark:text-amber-400" />
        <StatTile label="Unavailable" value={counts.out} tone="text-rose-600 dark:text-rose-400" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          title="Model details"
          className="lg:col-span-1 self-start"
          actions={isAdmin ? (
            <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditingModel(true)}>
              <Edit className="mr-1.5 h-3.5 w-3.5" />
              Edit
            </Button>
          ) : undefined}
        >
          <DetailList
            items={[
              { label: 'Brand', value: model.brand },
              { label: 'Model number', value: model.modelNumber, mono: true },
              { label: 'Power', value: typeLabel(model) },
              { label: powerFieldLabel(model), value: powerDetail(model) },
              { label: 'Description', value: model.description },
              { label: 'Created', value: model.createdAt ? formatDateShort(model.createdAt) : null },
            ]}
          />
        </Panel>

        <Panel
          title={`Assets (${assets.length})`}
          className="lg:col-span-2"
          actions={
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Serial or holder"
                  className="h-8 w-44 pl-8 text-sm"
                />
              </div>
              <Dropdown
                className="w-36 [&>button]:h-8"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All status' },
                  { value: 'available', label: 'Available' },
                  { value: 'assigned', label: 'Assigned' },
                  { value: 'assigned_pending', label: 'Pending approval' },
                  { value: 'unavailable', label: 'Unavailable' },
                  { value: 'broken', label: 'Broken' },
                  { value: 'completely_used', label: 'Completely used' },
                  { value: 'eol', label: 'EoL' },
                ]}
              />
            </div>
          }
        >
          {visibleAssets.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">No assets match</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Serial number</th>
                    <th>Assigned to</th>
                    <th>Ownership</th>
                    <th>Status</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAssets.map((asset) => {
                    const holder = asset.assignments?.[0]
                    return (
                      <tr key={asset.id}>
                        <td>
                          <Link to={`/devices/${asset.id}`} className="font-mono text-xs font-medium text-primary hover:underline">
                            {asset.serialNumber || asset.name}
                          </Link>
                        </td>
                        <td data-label="Assigned to">
                          {holder?.employee ? (
                            <div className="min-w-0">
                              <p className="truncate text-sm">{holder.employee.name}</p>
                              <p className="truncate text-xs text-muted-foreground">{holder.employee.email}</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td data-label="Ownership">{ownershipLabel(asset)}</td>
                        <td data-label="Status">
                          <Badge className={getStatusColor(asset.status)}>{statusLabel(asset.status)}</Badge>
                        </td>
                        <td className="is-actions">
                          <div className="flex justify-end gap-1">
                            {isAdmin && canReturn(asset.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                disabled={returningId === asset.id}
                                onClick={async () => {
                                  const label = asset.serialNumber || asset.name || 'this asset'
                                  if (!window.confirm(`Mark ${label} as returned? It will be available to assign again.`)) return
                                  setReturningId(asset.id)
                                  try {
                                    await returnAsset(asset.id)
                                    toast.success('Asset marked returned')
                                    refresh()
                                  } catch (error: any) {
                                    toast.error(error.response?.data?.message || error.message || 'Could not mark the asset returned')
                                  } finally {
                                    setReturningId(null)
                                  }
                                }}
                              >
                                Return
                              </Button>
                            )}
                            {isAdmin && canAssign(asset.status) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                onClick={() => setAssignTarget({
                                  id: asset.id,
                                  label: asset.serialNumber || asset.name,
                                  assignment: holder || null,
                                })}
                              >
                                {asset.status === 'available' ? 'Assign' : 'Reassign'}
                              </Button>
                            )}
                            <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                              <Link to={`/devices/${asset.id}`} aria-label="View asset">
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            {isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  aria-label="Edit asset"
                                  onClick={() => setEditAsset(asset)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  aria-label="Delete asset"
                                  onClick={() => {
                                    if (window.confirm(`Delete asset ${asset.serialNumber || asset.name}?`)) {
                                      deleteAsset.mutate(asset.id)
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <DeviceModelForm
        isOpen={editingModel}
        modelId={editingModel ? model.id : null}
        onClose={() => setEditingModel(false)}
        onSuccess={refresh}
      />
      <DeviceForm
        isOpen={isAddOpen || !!editAsset}
        onClose={() => {
          setIsAddOpen(false)
          setEditAsset(null)
        }}
        device={editAsset}
        defaultModelId={editAsset ? undefined : model.id}
        onSuccess={refresh}
      />
      <AssignmentModal
        isOpen={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        deviceId={assignTarget?.id || 0}
        deviceName={assignTarget?.label || ''}
        currentAssignment={assignTarget?.assignment}
        onSuccess={refresh}
      />
    </div>
  )
}
